import { useState, useEffect } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner, Modal } from "react-bootstrap";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { artistImageUrl, extractObjectId } from "../../../utils/utils";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";
import {
    FaCrown,
    FaUsers,
    FaSearch,
    FaTimes,
    FaTag,
    FaEdit,
    FaTrash,
    FaPlus,
    FaChevronLeft,
    FaChevronRight,
    FaExclamationTriangle,
    FaStar
} from "react-icons/fa";
import "./TopArtist.css";

const TopArtist = () => {
    const navigate = useNavigate();
    const pageTarget = usePageTargetSelection();
    const targetParams = pageTarget.selectedTargetParams;
    const selectedTargetLabel = pageTarget.selectedTargetLabel;

    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [topArtists, setTopArtists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit] = useState(20);
    const [debounceQuery, setDebounceQuery] = useState(searchQuery);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [artistToDelete, setArtistToDelete] = useState(null);

    const fetchTopArtists = async (page, limit) => {
        setLoading(true);
        try {
            const params = { limit, page, ...targetParams };

            let response = await allAPiServicesCall.getTopArtists(
                params,
                getAuthConfig()
            );

            const { totalPages, topArtistList } = response.data;

            if (topArtistList) {
                setTopArtists(topArtistList);
                setTotalPages(totalPages);
            } else {
                console.warn("No data found or data is not an array");
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("Failed to fetch top artists");
        }
    };

    const fetchSearchResults = async (query) => {
        setLoading(true);
        try {
            let response = await allAPiServicesCall.getSearchArtist(
                { query },
                getAuthConfig()
            );
            const searchList = response.data || [];
            if (searchList.length > 0) {
                setSearchResults(searchList);
            } else {
                setSearchResults([]);
                toast.info("No results found");
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error("Failed to fetch search artists");
        }
    };

    const addArtistToTop = async (artistId) => {
        setLoading(true);
        try {
            const payload = { artist_id: artistId, ...targetParams };
            await allAPiServicesCall.addTopArtist(
                payload,
                getAuthConfig(),
            );
            fetchTopArtists(currentPage, limit);
            toast.success(`Artist added to top for ${selectedTargetLabel}`);
            setLoading(false);
            setSearchQuery("")
        } catch (error) {
            setLoading(false);
            toast.error(error.message);
        }
    };

    const handleOnDelete = async () => {
        if (!artistToDelete) return;
        setLoading(true);
        try {
            let payload = { id: artistToDelete };
            await allAPiServicesCall.deleteTopArtist(payload, getAuthConfig());
            fetchTopArtists(currentPage, limit);
            toast.success("Artist deleted successfully");
            setShowDeleteModal(false);
        } catch (error) {
            toast.error("Failed to delete artist.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebounceQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    useEffect(() => {
        if (debounceQuery.trim()) {
            fetchSearchResults(debounceQuery);
        } else {
            fetchTopArtists(currentPage, limit);
        }
    }, [debounceQuery, currentPage, limit, targetParams.pageType, targetParams.genreSlug]);

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prevPage) => prevPage - 1);
        }
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
    };

    const confirmDelete = (artistId) => {
        setArtistToDelete(artistId);
        setShowDeleteModal(true);
    };

    return (
        <CommonLayout>
            <div className="top-artists-page">
                {/* Header Section */}
                <div className="top-header">
                    <div className="top-header-content">
                        <div className="top-header-icon">
                            <FaCrown />
                        </div>
                        <div className="top-header-text">
                            <h1 className="top-title">
                                Top Artists
                                {pageTarget.selectedTarget?.pageType === "genre" && (
                                    <span className="top-genre-tag">
                                        {pageTarget.selectedTarget?.genreSlug}
                                    </span>
                                )}
                            </h1>
                            <p className="top-subtitle">
                                Manage your top artists collection
                            </p>
                        </div>
                    </div>
                    <div className="top-stats">
                        <div className="top-stat-card">
                            <FaUsers className="top-stat-icon" />
                            <div className="top-stat-info">
                                <span className="top-stat-value">{topArtists.length}</span>
                                <span className="top-stat-label">Artists</span>
                            </div>
                        </div>
                        <div className="top-stat-card">
                            <FaStar className="top-stat-icon" />
                            <div className="top-stat-info">
                                <span className="top-stat-value">{totalPages}</span>
                                <span className="top-stat-label">Pages</span>
                            </div>
                        </div>
                    </div>
                </div>

                <PageTargetSelector
                    pageTarget={pageTarget}
                    subtitle="Switch between Homepage and Genre targets before curating top artists."
                />

                {/* Search Section */}
                <div className="top-search-section">
                    <div className="top-search-wrapper">
                        <FaSearch className="top-search-icon" />
                        <input
                            type="text"
                            className="top-search-input"
                            placeholder="Search for artists to add to Top Artists..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button
                                className="top-search-clear"
                                onClick={clearSearch}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    {searchQuery.trim() && (
                        <div className="top-search-badge">
                            <FaSearch />
                            Searching for "{searchQuery}"
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="top-content">
                    {loading ? (
                        <div className="top-loading">
                            <div className="top-loading-spinner">
                                <Spinner animation="border" />
                            </div>
                            <p className="top-loading-text">Loading artists...</p>
                        </div>
                    ) : searchQuery.trim() ? (
                        // Search Results
                        searchResults.length > 0 ? (
                            <div className="top-grid">
                                {searchResults.map((result, index) => (
                                    <div
                                        key={result.artist_id}
                                        className="top-card top-card-search"
                                    >
                                        <div className="top-card-image-wrapper">
                                            <img
                                                src={artistImageUrl(result)}
                                                alt={result?.artist_name || "Artist"}
                                                className="top-card-image"
                                                onClick={() => navigate(`/artist/albums-songs/${result.artist_id}`)}
                                            />
                                            <div className="top-card-overlay">
                                                <span className="top-card-rank">
                                                    #{index + 1}
                                                </span>
                                                <span className="top-card-badge top-card-badge-search">
                                                    <FaSearch /> Search
                                                </span>
                                            </div>
                                        </div>
                                        <div className="top-card-content">
                                            <h3 className="top-card-name">{result?.artist_name}</h3>
                                            <div className="top-card-actions">
                                                <button
                                                    className="top-action-btn top-action-btn-add"
                                                    onClick={() => addArtistToTop(result.artist_id)}
                                                >
                                                    <FaPlus /> Add to Top
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="top-empty">
                                <div className="top-empty-icon">
                                    <FaSearch />
                                </div>
                                <h3 className="top-empty-title">No results found</h3>
                                <p className="top-empty-text">
                                    Try searching with different keywords to find artists
                                </p>
                                <button
                                    className="top-empty-btn"
                                    onClick={clearSearch}
                                >
                                    <FaTimes /> Clear Search
                                </button>
                            </div>
                        )
                    ) : (
                        // Top Artists List
                        topArtists.length > 0 ? (
                            <>
                                <div className="top-grid">
                                    {topArtists.map((artist, index) => (
                                        <div
                                            key={artist.id || artist._id}
                                            className="top-card"
                                        >
                                            <div className="top-card-image-wrapper">
                                                <img
                                                    src={artistImageUrl(artist)}
                                                    alt={artist?.artist_name || "Artist"}
                                                    className="top-card-image"
                                                    onClick={() => navigate(`/artist/albums-songs/${artist.artist_id}`)}
                                                />
                                                <div className="top-card-overlay">
                                                    <span className="top-card-rank">
                                                        <FaCrown className="top-card-rank-icon" />
                                                        #{(currentPage - 1) * limit + index + 1}
                                                    </span>
                                                    <span className="top-card-badge">
                                                        <FaCrown /> Top
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="top-card-content">
                                                <h3 className="top-card-name">{artist?.artist_name}</h3>
                                                <div className="top-card-actions">
                                                    <button
                                                        className="top-action-btn top-action-btn-edit"
                                                        onClick={() => navigate(`/edit-artist/${artist.artist_id}`)}
                                                    >
                                                        <FaEdit /> Edit
                                                    </button>
                                                    <button
                                                        className="top-action-btn top-action-btn-delete"
                                                        onClick={() => confirmDelete(extractObjectId(artist?._id))}
                                                    >
                                                        <FaTrash /> Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="top-pagination">
                                        <button
                                            className="top-pagination-btn"
                                            onClick={handlePreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <FaChevronLeft /> Previous
                                        </button>
                                        <div className="top-pagination-info">
                                            <span className="top-pagination-current">{currentPage}</span>
                                            <span className="top-pagination-separator">of</span>
                                            <span className="top-pagination-total">{totalPages}</span>
                                        </div>
                                        <button
                                            className="top-pagination-btn"
                                            onClick={handleNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next <FaChevronRight />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="top-empty">
                                <div className="top-empty-icon">
                                    <FaCrown />
                                </div>
                                <h3 className="top-empty-title">No top artists found</h3>
                                <p className="top-empty-text">
                                    Search for artists above to add them to your top artists collection
                                </p>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
                className="top-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaExclamationTriangle style={{ color: '#EF4444', marginRight: '0.5rem' }} />
                        Confirm Deletion
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to remove this artist from the top artists list?
                    This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="top-modal-btn top-modal-btn-cancel"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="top-modal-btn top-modal-btn-delete"
                        onClick={handleOnDelete}
                    >
                        <FaTrash /> Delete
                    </button>
                </Modal.Footer>
            </Modal>
        </CommonLayout>
    );
};

export default TopArtist;
